import { batchGetMonthlyMinutes } from './billingDataService';
import {
  getActiveEncountersInEffectiveDateRange,
  REPORT_ENCOUNTER_QUERY_CAP,
  ENCOUNTER_TYPES,
  AUTOMATED_ENCOUNTER_TYPES,
} from './patientEncounterService';
const ENCOUNTER_LABEL = new Map(ENCOUNTER_TYPES.map((t) => [t.value, t.label]));
const AUTO_ENCOUNTER = new Set(AUTOMATED_ENCOUNTER_TYPES.map((t) => t.value));
const LOW_THRESHOLD = 20;

export function labelForEncounterType(code) {
  return ENCOUNTER_LABEL.get(code) || code || '—';
}

/**
 * Sum monthly billingData rollups across a set of yyyy-mm keys for every
 * patient. This is the authoritative source for headline minutes/encounter
 * counts (the raw encounter query is capped per patient).
 * @param {string[]} patientIds
 * @param {string[]} ymKeys
 * @returns {Promise<Map<string, {totalMinutes:number, encounterCountTotal:number, encounterCounts:Record<string,number>}>>}
 */
export async function sumMonthlyRollups(patientIds, ymKeys) {
  const totals = new Map();
  (patientIds || []).forEach((pid) =>
    totals.set(pid, { totalMinutes: 0, encounterCountTotal: 0, encounterCounts: {} }),
  );
  if (!patientIds?.length || !ymKeys?.length) return totals;

  for (const ym of ymKeys) {
    // eslint-disable-next-line no-await-in-loop
    const monthMap = await batchGetMonthlyMinutes(patientIds, ym);
    monthMap.forEach((v, pid) => {
      const t = totals.get(pid) || { totalMinutes: 0, encounterCountTotal: 0, encounterCounts: {} };
      t.totalMinutes += v.totalMinutes || 0;
      t.encounterCountTotal += v.encounterCountTotal || 0;
      Object.entries(v.encounterCounts || {}).forEach(([k, c]) => {
        t.encounterCounts[k] = (t.encounterCounts[k] || 0) + c;
      });
      totals.set(pid, t);
    });
  }
  return totals;
}

/** Minutes-distribution buckets (CPT-aligned: 99457 first 20 min, 99458 each +20). */
function minuteBuckets(minutesList) {
  const buckets = [
    { key: 'zero', label: '0 min', min: -1, max: 0, count: 0 },
    { key: 'lt20', label: '1–19 min', min: 1, max: 19, count: 0 },
    { key: '20to39', label: '20–39 min', min: 20, max: 39, count: 0 },
    { key: '40to59', label: '40–59 min', min: 40, max: 59, count: 0 },
    { key: 'gte60', label: '60+ min', min: 60, max: Infinity, count: 0 },
  ];
  minutesList.forEach((mins) => {
    const m = Math.round(mins);
    const b = buckets.find((x) => m >= x.min && m <= x.max);
    if (b) b.count += 1;
  });
  return buckets;
}

/**
 * Care Time Summary dataset.
 * @param {{ rollupByPatient: Map, rawEncounters: object[], rawTruncated: boolean }} input
 */
export function buildCareTimeSummary({ rollupByPatient, rawEncounters = [], rawTruncated = false }) {
  let totalMinutes = 0;
  let totalEncounters = 0;
  let patientsTouched = 0;
  let patientsBillable = 0;
  const perPatientMinutes = [];

  rollupByPatient.forEach((v) => {
    totalMinutes += v.totalMinutes;
    totalEncounters += v.encounterCountTotal;
    if (v.totalMinutes > 0 || v.encounterCountTotal > 0) patientsTouched += 1;
    if (v.totalMinutes >= LOW_THRESHOLD) patientsBillable += 1;
    perPatientMinutes.push(v.totalMinutes);
  });

  // Minutes-by-type comes from the raw encounters (rollups only store counts).
  const byTypeMap = new Map();
  rawEncounters.forEach((enc) => {
    const minsRaw =
      typeof enc.sessionMinutes === 'number' ? enc.sessionMinutes : parseInt(String(enc.sessionMinutes), 10);
    const mins = Number.isFinite(minsRaw) ? minsRaw : 0;
    const k = enc.encounterType || '—';
    const prev = byTypeMap.get(k) || { count: 0, minutes: 0 };
    prev.count += 1;
    prev.minutes += mins;
    byTypeMap.set(k, prev);
  });
  const byType = [...byTypeMap.entries()]
    .map(([typ, agg]) => ({
      encounterType: typ,
      label: labelForEncounterType(typ),
      automated: Boolean(AUTO_ENCOUNTER.has(typ)),
      count: agg.count,
      minutes: agg.minutes,
    }))
    .sort((a, b) => b.minutes - a.minutes);

  return {
    kpis: {
      totalMinutes,
      totalEncounters,
      patientsTouched,
      patientsBillable,
      avgMinutesPerPatient: rollupByPatient.size ? totalMinutes / rollupByPatient.size : 0,
      lowThreshold: LOW_THRESHOLD,
    },
    byType,
    histogram: minuteBuckets(perPatientMinutes),
    rawTruncated,
    cap: REPORT_ENCOUNTER_QUERY_CAP,
  };
}

/** Re-export the raw fetch (detail-only / minutes-by-type). */
export async function fetchRawEncounters(tenantId, patientIds, startStr, endStr) {
  if (!tenantId || !patientIds?.length) return { rows: [], truncated: false };
  const rows = await getActiveEncountersInEffectiveDateRange(tenantId, patientIds, startStr, endStr);
  const perPid = new Map();
  rows.forEach((r) => perPid.set(r.patientId, (perPid.get(r.patientId) || 0) + 1));
  let truncated = false;
  perPid.forEach((cnt) => {
    if (cnt >= REPORT_ENCOUNTER_QUERY_CAP) truncated = true;
  });
  return { rows, truncated };
}

function formatPatientName(patient) {
  if (!patient) return 'Patient';
  const first = (patient.firstName || '').trim();
  const pref = (patient.preferredName || '').trim();
  const last = (patient.lastName || '').trim();
  const mid = pref ? ` (${pref})` : '';
  const left = `${first}${mid}`.trim();
  return [left, last].filter(Boolean).join(' ').trim() || patient.id || 'Patient';
}

/**
 * Care Time by Patient dataset (rollup-sourced, current vs. prior block).
 * @param {{ currentByPatient: Map, priorByPatient: Map, patientById: Map }} input
 */
export function buildCareTimeByPatient({ currentByPatient, priorByPatient, patientById }) {
  const rows = [];
  let totalMinutes = 0;
  let patientsBillable = 0;
  const perPatientMinutes = [];

  currentByPatient.forEach((v, pid) => {
    const prior = priorByPatient.get(pid) || { totalMinutes: 0, encounterCountTotal: 0 };
    const minutesCur = v.totalMinutes;
    totalMinutes += minutesCur;
    if (minutesCur >= LOW_THRESHOLD) patientsBillable += 1;
    perPatientMinutes.push(minutesCur);
    rows.push({
      patientId: pid,
      name: formatPatientName(patientById.get(pid)),
      minutesCur,
      minutesPrev: prior.totalMinutes,
      encounters: v.encounterCountTotal,
      delta: minutesCur - prior.totalMinutes,
    });
  });

  rows.sort((a, b) => b.minutesCur - a.minutesCur);

  return {
    rows,
    kpis: {
      patients: rows.length,
      totalMinutes,
      patientsBillable,
      avgMinutesPerPatient: rows.length ? totalMinutes / rows.length : 0,
      lowThreshold: LOW_THRESHOLD,
    },
    histogram: minuteBuckets(perPatientMinutes),
  };
}
